/**
 * <hugh-search>: suggestions for snippets/hugh-header-search.liquid.
 * Empty field → the server-rendered start panel (popular searches, best sellers). Two or more
 * characters → Shopify's predictive search (`${data-url}.json`, the only request), rendered with
 * DOM methods and textContent only. Arrow keys move through the options, Enter opens one, Escape
 * closes. Without JS the form is a plain GET /search.
 */
const MIN = 2;
const WAIT = 180;

class HughSearch extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {AbortController | undefined} */
  #request;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Map<string, any>} */
  #cache = new Map();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.input?.addEventListener('focus', () => this.#open(), { signal });
    this.input?.addEventListener('input', () => this.#onInput(), { signal });
    this.addEventListener('keydown', (event) => this.#onKey(event), { signal });
    document.addEventListener('pointerdown', (event) => { if (event.target instanceof Node && !this.contains(event.target)) this.#close(); }, { signal });
    this.#rotate(signal);
    this.addEventListener('focusout', (event) => { if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#close(); }, { signal });
  }

  /**
   * While the field is empty and not in use, its placeholder cycles through the popular searches
   * ("Try “Diabetic socks”"), so it invites a search and shows what works. @param {AbortSignal} signal
   */
  #rotate(signal) {
    const input = this.input;
    const terms = (this.dataset.examples || '').split('<br />').map((l) => l.split('|')[0].trim()).filter(Boolean);
    const pattern = this.dataset.try || '';
    if (!input || !terms.length || !pattern.includes('[q]')) return;
    const base = input.placeholder;
    let i = 0;
    const timer = window.setInterval(() => {
      if (document.activeElement === input || input.value) return;
      input.placeholder = pattern.replace('[q]', terms[i % terms.length]);
      i += 1;
    }, 2600);
    input.addEventListener('focus', () => { input.placeholder = base; }, { signal });
    signal.addEventListener('abort', () => window.clearInterval(timer));
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#request?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  get input() {
    return this.querySelector('.hugh-hs__input');
  }

  get #panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.hugh-hs__panel'));
  }

  get #results() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-hs-results]'));
  }

  get #start() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-hs-start]'));
  }

  /** @param {string} key */
  #t(key) {
    try {
      return JSON.parse(this.dataset.i18n || '{}')[key] || '';
    } catch {
      return '';
    }
  }

  #open() {
    const panel = this.#panel;
    if (!panel) return;
    const term = this.#term();
    if (term.length < MIN && !this.#start?.children.length) return;
    panel.hidden = false;
    this.input?.setAttribute('aria-expanded', 'true');
  }

  #close() {
    if (this.#panel) this.#panel.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
  }

  /** The typed term, trimmed and capped, so nothing odd reaches the request. */
  #term() {
    return (this.input?.value || '').trim().slice(0, 80);
  }

  #onInput() {
    clearTimeout(this.#timer);
    const term = this.#term();
    if (term.length < MIN) {
      this.#show(false);
      this.#open();
      return;
    }
    this.#timer = setTimeout(() => this.#fetch(term), WAIT);
  }

  /** @param {boolean} results */
  #show(results) {
    if (this.#results) this.#results.hidden = !results;
    if (this.#start) this.#start.hidden = results;
  }

  /** @param {string} term */
  async #fetch(term) {
    const cached = this.#cache.get(term);
    if (cached) return this.#render(term, cached);
    this.#request?.abort();
    this.#request = new AbortController();
    const url = new URL(`${this.dataset.url}.json`, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query,collection,product');
    url.searchParams.set('resources[limit]', '6');
    // Sold-out socks as Theme settings › Hugh: product lists says (show, last or hide: Shopify's own values).
    url.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailable || 'last');
    try {
      const response = await fetch(url, { signal: this.#request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = (await response.json())?.resources?.results || {};
      this.#cache.set(term, data);
      if (term === this.#term()) this.#render(term, data);
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') this.#render(term, {});
    }
  }

  /**
   * @param {string} term
   * @param {{ queries?: any[], collections?: any[], products?: any[] }} data
   */
  #render(term, data) {
    const box = this.#results;
    if (!box) return;
    box.replaceChildren();
    const queries = (data.queries || []).slice(0, 4);
    const collections = (data.collections || []).slice(0, 3);
    const products = (data.products || []).slice(0, 5);

    if (queries.length) {
      box.append(this.#head(this.#t('searches')));
      box.append(this.#list(queries.map((q) => this.#row(q.url, String(q.text || ''), term, 'search'))));
    }
    if (collections.length) {
      box.append(this.#head(this.#t('collections')));
      box.append(this.#list(collections.map((c) => this.#row(c.url, String(c.title || ''), term, 'chevron-right'))));
    }
    if (products.length) {
      box.append(this.#head(this.#t('products')));
      box.append(this.#list(products.map((p) => this.#product(p))));
    }
    if (!queries.length && !collections.length && !products.length) {
      const none = document.createElement('p');
      none.className = 'hugh-hs__none';
      none.textContent = this.#t('none');
      box.append(none);
    }
    const all = this.#link(`${this.dataset.searchUrl}?q=${encodeURIComponent(term)}`, 'hugh-hs__all');
    all.textContent = this.#t('all').replace('[q]', term);
    box.append(all);

    this.#show(true);
    this.#open();
    const status = this.querySelector('[data-hs-status]');
    if (status) status.textContent = `${products.length} ${this.#t('products')}`;
  }

  /** @param {string} text */
  #head(text) {
    const p = document.createElement('p');
    p.className = 'hugh-hs__head';
    p.textContent = text;
    return p;
  }

  /** @param {HTMLElement[]} items */
  #list(items) {
    const ul = document.createElement('ul');
    ul.className = 'hugh-hs__list';
    ul.setAttribute('role', 'list');
    for (const item of items) {
      const li = document.createElement('li');
      li.append(item);
      ul.append(li);
    }
    return ul;
  }

  /** Same-origin paths only: suggestion URLs come from Shopify, but never trust a full URL. @param {string} href @param {string} className */
  #link(href, className) {
    const a = document.createElement('a');
    a.className = className;
    a.setAttribute('role', 'option');
    a.id = `${this.input?.id || 'hs'}-o${this.querySelectorAll('[role="option"]').length}`;
    a.href = typeof href === 'string' && href.startsWith('/') && !href.startsWith('//') ? href : this.dataset.searchUrl || '/search';
    return a;
  }

  /**
   * A row with the typed part plain and the rest bold, like most search boxes.
   * @param {string} href @param {string} text @param {string} term @param {string} icon
   */
  #row(href, text, term, icon) {
    const a = this.#link(href, 'hugh-hs__row');
    const svg = this.querySelector(`.hugh-hs__submit .hugh-icon`)?.cloneNode(true);
    if (svg instanceof SVGElement && icon === 'search') a.append(svg);
    else a.append(document.createElement('span'));
    const label = document.createElement('span');
    const at = text.toLowerCase().indexOf(term.toLowerCase());
    if (at === 0) {
      label.append(document.createTextNode(text.slice(0, term.length)));
      const mark = document.createElement('mark');
      mark.textContent = text.slice(term.length);
      label.append(mark);
    } else {
      label.textContent = text;
    }
    a.append(label);
    return a;
  }

  /** @param {any} product */
  #product(product) {
    const a = this.#link(product.url, 'hugh-hs__product');
    const src = product.featured_image?.url || product.image;
    if (typeof src === 'string' && src) {
      const img = document.createElement('img');
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '120');
      img.src = url.href;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      img.className = 'hugh-hs__img';
      a.append(img);
    } else {
      a.append(document.createElement('span'));
    }
    const name = document.createElement('span');
    name.className = 'hugh-hs__name';
    name.textContent = String(product.title || '').split(', ')[0];
    const price = document.createElement('span');
    price.className = 'hugh-hs__price';
    const value = Number(product.price);
    price.textContent = Number.isFinite(value)
      ? new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(value)
      : '';
    a.append(name, price);
    return a;
  }

  /** @param {KeyboardEvent} event */
  #onKey(event) {
    const panel = this.#panel;
    if (event.key === 'Escape') {
      this.#close();
      if (!this.#term() && this.hasAttribute('data-open') && this.id) {
        const toggle = document.querySelector(`[data-hs-toggle][aria-controls="${this.id}"]`);
        if (toggle instanceof HTMLElement) {
          foldRow(this, toggle, false);
          toggle.focus();
          return;
        }
      }
      this.input?.focus();
      return;
    }
    if (!panel || panel.hidden || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const options = [...panel.querySelectorAll('[role="option"]')].filter((o) => o instanceof HTMLElement && o.offsetParent !== null);
    if (!options.length) return;
    event.preventDefault();
    const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
    const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
    options.forEach((o, i) => o.setAttribute('aria-selected', String(i === next)));
    const target = /** @type {HTMLElement} */ (options[next]);
    if (!target.id) target.id = `${this.input?.id || 'hs'}-s${next}`;
    this.input?.setAttribute('aria-activedescendant', target.id);
    target.scrollIntoView({ block: 'nearest' });
    if (document.activeElement !== this.input) target.focus();
  }
}

if (!customElements.get('hugh-search')) customElements.define('hugh-search', HughSearch);

/**
 * Phones: the search button beside the menu (snippets/hugh-search-toggle) opens the folded search row
 * and puts the cursor in it; pressing it again, or Escape in an empty field, folds it.
 * @param {HughSearch} row @param {HTMLElement} toggle @param {boolean} open
 */
function foldRow(row, toggle, open) {
  row.toggleAttribute('data-open', open);
  toggle.setAttribute('aria-expanded', String(open));
  if (open) row.input?.focus();
}

document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-hs-toggle]') : null;
  if (!(toggle instanceof HTMLElement)) return;
  const row = document.getElementById(toggle.getAttribute('aria-controls') || '');
  if (!(row instanceof HughSearch)) return;
  event.preventDefault(); // the toggle is a link to the search page for when this script is missing
  foldRow(row, toggle, !row.hasAttribute('data-open'));
});

// The link acts as a button (role="button"), so Space opens it too.
document.addEventListener('keydown', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-hs-toggle]') : null;
  if (!(toggle instanceof HTMLElement) || event.key !== ' ') return;
  event.preventDefault();
  toggle.click();
});

// Enter on a highlighted option follows it instead of submitting the typed term.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('hugh-hs__form')) return;
  const host = form.closest('hugh-search');
  const selected = host?.querySelector('.hugh-hs__panel:not([hidden]) [role="option"][aria-selected="true"]');
  if (selected instanceof HTMLAnchorElement) {
    event.preventDefault();
    window.location.assign(selected.href);
  }
});
